import React, { useState, useEffect, useMemo } from 'react';
import { Wifi, RefreshCw, Eye, Send, AlertTriangle } from 'lucide-react';
import api from '../services/api';

const fmt = (t) => (t ? new Date(t).toLocaleString('es-MX') : '—');

const CONN = {
  wifi_sucursal: { c: '#059669', bg: '#d1fae5', label: 'WiFi sucursal' },
  samsara: { c: '#7c3aed', bg: '#ede9fe', label: 'Samsara' },
  datos_celular: { c: '#ca8a04', bg: '#fef3c7', label: 'Datos' },
  desconocido: { c: '#6b7280', bg: '#f3f4f6', label: '?' },
};
const connOf = (k) => CONN[k] || CONN.desconocido;
// Accionable = tiene IP LAN descubierta (el escaneo/mikrotik la encontró -> el
// server la alcanza). El tipo de conexion (uplink) NO define alcanzabilidad:
// un equipo por "Datos"/matriz puede estar en una LAN ruteada.
const isActionable = (m) => !!m.local_ip_live;

const MdvrWifi = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sucursal, setSucursal] = useState('');
  const [soloAccionables, setSoloAccionables] = useState(false);
  const [ssid, setSsid] = useState('');
  const [key, setKey] = useState('');
  const [reboot, setReboot] = useState(false);
  const [cur, setCur] = useState({});
  const [res, setRes] = useState({});
  const [busy, setBusy] = useState({});
  const [discovering, setDiscovering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/mdvr/wifi-targets');
      setRows(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const discover = async (mode) => {
    setDiscovering(true);
    try {
      const body = { mode };
      if (mode === 'sucursal') body.sucursal_name = sucursal;
      const r = await api.post('/api/mdvr/discover', body);
      const found = r.data.found || r.data.confirmados || [];
      alert(`Descubrimiento (${mode}): ${found.length} equipos con IP fresca`);
      await load();
    } catch (e) {
      alert('Error en descubrimiento: ' + (e.response?.data?.detail || e.message));
    } finally { setDiscovering(false); }
  };

  const sucursales = useMemo(
    () => [...new Set(rows.map((r) => r.sucursal_actual).filter(Boolean))].sort(), [rows]);

  const shown = rows.filter((r) =>
    (!sucursal || r.sucursal_actual === sucursal) &&
    (!soloAccionables || isActionable(r)));

  const nAcc = rows.filter(isActionable).length;

  const readOne = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.get(`/api/mdvr/${id}/wifi`);
      setCur((c) => ({ ...c, [id]: r.data }));
    } catch (e) {
      setCur((c) => ({ ...c, [id]: { success: false, error: 'no alcanzable' } }));
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const applyOne = async (id) => {
    if (!ssid || !key) { alert('Pon SSID y clave arriba primero.'); return; }
    const msg = `¿Cambiar WiFi de ${id} a "${ssid}"?` +
      (reboot ? '\n\n⚠️ Con REINICIO: salta a la nueva red. Debe estar transmitiéndose.'
              : '\n\n(Sin reinicio: queda staged.)');
    if (!window.confirm(msg)) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.post(`/api/mdvr/${id}/wifi`, { ssid, key, reboot });
      setRes((s) => ({ ...s, [id]: r.data }));
    } catch (e) {
      setRes((s) => ({ ...s, [id]: { success: false, error: e.response?.data?.detail || 'error' } }));
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const applyBulk = async () => {
    if (!ssid || !key) { alert('Pon SSID y clave arriba primero.'); return; }
    const targets = shown.filter(isActionable);
    if (targets.length === 0) { alert('No hay equipos accionables (con IP descubierta) en la vista.'); return; }
    if (!window.confirm(`¿Cambiar WiFi de ${targets.length} equipos accionables a "${ssid}"?` +
      (reboot ? '\n\n⚠️ Con REINICIO de todos.' : '\n\n(Sin reinicio: staged.)'))) return;
    setLoading(true);
    try {
      const r = await api.post('/api/mdvr/wifi/bulk',
        { device_ids: targets.map((m) => m.device_id), ssid, key, reboot });
      const map = {};
      (r.data.resultados || []).forEach((x) => { map[x.device_id] = x; });
      setRes((s) => ({ ...s, ...map }));
      alert(`Listo: ${r.data.ok}/${r.data.total} OK`);
    } finally { setLoading(false); }
  };

  const resLabel = (r) => {
    if (!r) return null;
    if (!r.success) return <span style={S.bad}>✕ {r.error || `status ${r.status}`}</span>;
    if (r.rebooted) return <span style={S.ok}>✓ Aplicado + reinicio</span>;
    if (r.reboot_required) return <span style={S.warn}>● Staged (falta reiniciar)</span>;
    return <span style={S.ok}>✓ Guardado</span>;
  };

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Wifi size={26} /> WiFi de MDVR</h1>
          <p style={S.subtitle}>Estado actual. {nAcc} con IP LAN descubierta (accionables) de {rows.length} equipos.</p>
        </div>
        <button style={S.refreshBtn} onClick={load}><RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar</button>
      </header>

      <div style={S.warnBox}>
        <AlertTriangle size={18} color="#b45309" />
        <span>Se puede actuar sobre equipos con <b>IP LAN descubierta</b> (el server los alcanza) — sin importar
          su tipo de uplink. Si un equipo no la tiene, corre <b>Descubrir IP local</b> abajo. El cambio queda
          <b> staged</b> hasta reiniciar.</span>
      </div>

      <div style={S.form}>
        <div style={S.field}><label style={S.lab}>Nuevo SSID</label>
          <input style={S.input} value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="PFS_CAM" /></div>
        <div style={S.field}><label style={S.lab}>Clave</label>
          <input style={S.input} value={key} onChange={(e) => setKey(e.target.value)} placeholder="clave WPA2" /></div>
        <label style={S.check}>
          <input type="checkbox" checked={reboot} onChange={(e) => setReboot(e.target.checked)} /> Reiniciar al aplicar
        </label>
      </div>

      <div style={S.bulkRow}>
        <select style={S.select} value={sucursal} onChange={(e) => setSucursal(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {sucursales.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={S.check}>
          <input type="checkbox" checked={soloAccionables} onChange={(e) => setSoloAccionables(e.target.checked)} /> Solo accionables
        </label>
        {(() => { const t = shown.filter(isActionable).length; return (
          <button style={{ ...S.bulkBtn, opacity: t ? 1 : 0.5 }} disabled={!t} onClick={applyBulk}>
            <Send size={15} /> Aplicar a accionables mostrados ({t})
          </button>
        ); })()}
      </div>

      <div style={S.discRow}>
        <span style={S.discLab}>Descubrir IP local {discovering && <RefreshCw size={13} className="spin" />}:</span>
        <button style={S.discBtn} disabled={discovering} onClick={() => discover('refresh')}>Refrescar conocidas</button>
        {sucursal && <button style={S.discBtn} disabled={discovering} onClick={() => discover('sucursal')}>Escanear {sucursal}</button>}
        <button style={S.discBtn} disabled={discovering} onClick={() => discover('scan_all')}>Escanear todo (lento)</button>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Equipo</th><th style={S.th}>Conexión</th><th style={S.th}>Sucursal actual</th>
            <th style={S.th}>IP LAN (live)</th><th style={S.th}>SSID actual</th>
            <th style={S.th}>Resultado</th><th style={S.th}>Acciones</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} style={S.tdEmpty}>Sin equipos en este filtro</td></tr>}
            {shown.map((m) => {
              const id = m.device_id;
              const c = cur[id];
              const con = connOf(m.connection_type);
              const act = isActionable(m);
              return (
                <tr key={id} style={{ ...S.tr, opacity: act ? 1 : 0.6 }}>
                  <td style={S.td}><b>{m.numero_unidad ? `MDVR ${m.numero_unidad}` : id}</b><div style={S.sub}>{id}</div></td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: con.bg, color: con.c }}>{con.label}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: m.is_current ? '#059669' : '#9ca3af' }}>
                      {m.is_current ? '● online' : '○ offline'}</span>
                  </td>
                  <td style={S.td}>{m.sucursal_actual || '—'}</td>
                  <td style={S.td}>{m.local_ip_live
                    ? <b style={{ color: '#059669' }}>{m.local_ip_live}</b>
                    : <span style={{ color: '#9ca3af' }}>{act ? 'sin descubrir' : '—'}</span>}</td>
                  <td style={S.td}>{c ? (c.success ? <b>{c.ssid}</b> : <span style={S.bad}>{c.error || 'error'}</span>) : '—'}</td>
                  <td style={S.td}>{resLabel(res[id])}</td>
                  <td style={S.td}>
                    <button style={S.btnRead} disabled={busy[id] || !act} onClick={() => readOne(id)}><Eye size={13} /> Leer</button>
                    <button style={S.btnApply} disabled={busy[id] || !act} onClick={() => applyOne(id)}><Send size={13} /> Aplicar</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  warnBox: { display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 },
  form: { display: 'flex', gap: 14, alignItems: 'flex-end', background: 'white', padding: 14, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', marginBottom: 12, flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  lab: { fontSize: 12, color: '#6b7280' },
  input: { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, minWidth: 200 },
  check: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' },
  bulkRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' },
  select: { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, minWidth: 240 },
  bulkBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  discRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  discLab: { fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 },
  discBtn: { padding: '6px 12px', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  tableWrap: { background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', background: '#f9fafb', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', color: '#374151', verticalAlign: 'top' },
  sub: { fontSize: 11, color: '#9ca3af' },
  tdEmpty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 },
  btnRead: { padding: '5px 10px', marginRight: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnApply: { padding: '5px 10px', background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },
  ok: { color: '#059669', fontWeight: 600 },
  warn: { color: '#b45309', fontWeight: 600 },
  bad: { color: '#dc2626' },
};

export default MdvrWifi;
